import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import '../styles/SelfRegister.css';

const SelfRegister = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [step, setStep] = useState('welcome'); // welcome -> camera -> details -> success
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [photo, setPhoto] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
        setError('');
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Could not access camera. Please allow camera access and try again.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0);

    // Convert to base64 with good quality
    const photoData = canvas.toDataURL('image/jpeg', 0.8);
    setPhoto(photoData);
    setStep('details');
    stopCamera();
  };

  const retakePhoto = () => {
    setPhoto(null);
    setStep('camera');
    startCamera();
  };

  const handleRegister = async () => {
    // Validation
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    if (password.length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!photo) {
      setError('Please take a photo');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/register-self`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: username.trim(),
          password,
          profilePicture: photo
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      // Store auth token
      localStorage.setItem('token', data.token);
      localStorage.setItem('userId', data.userId);
      localStorage.setItem('username', data.username);

      setStep('success');

      // Auto-redirect to trading app after 3 seconds
      setTimeout(() => {
        navigate('/app');
      }, 3000);

    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="self-register">
      <div className="register-container">

        {/* WELCOME STEP */}
        {step === 'welcome' && (
          <div className="register-step welcome-step">
            <h1>🎉 Welcome to Stock Market Party!</h1>
            <p className="welcome-text">
              Get ready for an exciting night of virtual trading, market events, and competition!
            </p>
            <div className="welcome-features">
              <div className="feature">
                <span className="feature-icon">💰</span>
                <p>Start with $10,000</p>
              </div>
              <div className="feature">
                <span className="feature-icon">📈</span>
                <p>Trade real stocks</p>
              </div>
              <div className="feature">
                <span className="feature-icon">🏆</span>
                <p>Compete for prizes</p>
              </div>
              <div className="feature">
                <span className="feature-icon">⚡</span>
                <p>Live market events</p>
              </div>
            </div>
            <button
              onClick={() => {
                setStep('camera');
                setTimeout(startCamera, 100);
              }}
              className="register-button primary-button"
            >
              Get Started →
            </button>
          </div>
        )}

        {/* CAMERA STEP */}
        {step === 'camera' && (
          <div className="register-step camera-step">
            <h2>📸 Take Your Photo</h2>
            <p className="step-desc">This will be your profile picture on the leaderboard</p>

            <div className="camera-container">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="camera-video"
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="button-group">
              {!cameraActive ? (
                <button onClick={startCamera} className="register-button secondary-button">
                  📷 Enable Camera
                </button>
              ) : (
                <>
                  <button onClick={capturePhoto} className="register-button primary-button">
                    📸 Take Photo
                  </button>
                  <button onClick={() => setStep('welcome')} className="register-button text-button">
                    ← Back
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* DETAILS STEP */}
        {step === 'details' && (
          <div className="register-step details-step">
            <h2>✏️ Create Your Account</h2>

            {/* Photo Preview */}
            <div className="photo-preview">
              <img src={photo} alt="Your photo" />
              <button onClick={retakePhoto} className="retake-button">
                🔄 Retake
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-container">
              <div className="form-group">
                <label>Username:</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username..."
                  maxLength="20"
                  className="form-input"
                  autoComplete="off"
                />
              </div>

              <div className="form-group">
                <label>Password:</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 4 characters..."
                  className="form-input"
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label>Confirm Password:</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password..."
                  className="form-input"
                  autoComplete="new-password"
                />
              </div>

              <button
                onClick={handleRegister}
                disabled={loading || !username || !password || !confirmPassword}
                className="register-button primary-button"
              >
                {loading ? '⏳ Creating Account...' : '🚀 Join the Party!'}
              </button>

              <button
                onClick={retakePhoto}
                className="register-button text-button"
              >
                ← Back to Photo
              </button>
            </div>
          </div>
        )}

        {/* SUCCESS STEP */}
        {step === 'success' && (
          <div className="register-step success-step">
            <div className="success-animation">
              <div className="success-checkmark">✅</div>
            </div>
            <h2>🎉 You're In!</h2>
            <p className="success-text">
              Welcome, <strong>{username}</strong>!
            </p>
            <p className="success-subtext">
              Starting you with $10,000 virtual cash...
            </p>
            <div className="loading-dots">
              <span>.</span><span>.</span><span>.</span>
            </div>
            <p className="redirect-text">
              Redirecting to trading app...
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default SelfRegister;
